import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Button, Container, FormControl, FormLabel, 
  Heading, Input, VStack, Textarea, Switch, FormHelperText,
  HStack, useToast, Divider, SimpleGrid, IconButton,
  Text, Flex, Spinner, Alert, AlertIcon, Tab, TabList,
  TabPanel, TabPanels, Tabs, Badge, Modal, ModalOverlay,
  ModalContent, ModalHeader, ModalFooter, ModalBody, ModalCloseButton,
  useDisclosure, Code, Card, CardBody, CardHeader,
  useColorModeValue, InputGroup, InputLeftElement, InputRightElement,
  Tooltip, FormErrorMessage, Stack, Progress, Skeleton,
  SkeletonText, Icon, Menu, MenuButton, MenuList, MenuItem,
  AlertDialog, AlertDialogOverlay, AlertDialogContent,
  AlertDialogHeader, AlertDialogBody, AlertDialogFooter,
  Select, AccordionItem, Accordion, AccordionButton,
  AccordionPanel, AccordionIcon, Table, Thead, Tbody,
  Tr, Th, Td, TableContainer, Wrap, WrapItem
} from '@chakra-ui/react';
import { 
  ArrowBackIcon, 
  AddIcon, 
  DeleteIcon, 
  EditIcon,
  CopyIcon,
  LinkIcon,
  CheckIcon,
  InfoIcon,
  ViewIcon,
  ChevronDownIcon,
  WarningIcon,
  RepeatIcon,
  CalendarIcon
} from '@chakra-ui/icons';
import { Link as RouterLink, useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import apiClient from '../../../../utils/apiClient';

const MotionBox = motion(Box);
const MotionCard = motion(Card);
const MotionFlex = motion(Flex);

const TaxonomyEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [termToDelete, setTermToDelete] = useState(null);
  const cancelRef = useRef();
  
  // Color mode values
  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.600', 'gray.300');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  
  const [loading, setLoading] = useState(true);
  const [savingTaxonomy, setSavingTaxonomy] = useState(false);
  const [savingTerm, setSavingTerm] = useState(false);
  const [loadingLocales, setLoadingLocales] = useState(false);
  const [error, setError] = useState(null);
  const [taxonomy, setTaxonomy] = useState(null);
  const [terms, setTerms] = useState([]);
  const [locales, setLocales] = useState([]);
  const [errors, setErrors] = useState({});
  const [termErrors, setTermErrors] = useState({});
  
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
  });
  
  // Updated term form data to support multilingual
  const [termFormData, setTermFormData] = useState({
    slug: '',
    parent_id: null,
    sort_order: 0,
    status: 'active',
    translations: []
  });
  
  const [editingTermId, setEditingTermId] = useState(null);

  // Get tenant ID from session storage
  const user = JSON.parse(sessionStorage.getItem('user') || '{}');
  const tenantId = user?.tenant_ids?.[0];

  // Generate slug from name
  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  // Fetch available locales
  const fetchLocales = async () => {
    try {
      setLoadingLocales(true);
      // Get sites for this tenant first
      const sitesResponse = await apiClient(`/sites/${tenantId}`);
      const sites = sitesResponse || [];
      
      if (sites.length > 0) {
        // Get locales for the first site (you might want to make this configurable)
        const localesResponse = await apiClient(`/locales/${sites[0].id}`);
        setLocales(localesResponse || []);
      }
    } catch (err) {
      console.error('Error fetching locales:', err);
      setLocales([]);
    } finally {
      setLoadingLocales(false);
    }
  };

  const fetchTaxonomy = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!tenantId) {
        setError('No tenant ID found. Please log in again.');
        setLoading(false);
        return;
      }

      const taxonomyResponse = await apiClient(`/taxonomies/${tenantId}/${id}`);
      
      if (!taxonomyResponse) {
        throw new Error('Taxonomy not found');
      }

      const taxonomyData = taxonomyResponse.taxonomy || taxonomyResponse;
      setTaxonomy(taxonomyData);
      setFormData({
        name: taxonomyData.name || '',
        slug: taxonomyData.slug || '',
        description: taxonomyData.description || '',
      });

      // Fetch taxonomy terms
      const termsResponse = await apiClient(`/taxonomies/${tenantId}/${id}/terms`);
      if (termsResponse) {
        const termsData = termsResponse.terms || termsResponse;
        setTerms(Array.isArray(termsData) ? termsData : []);
      } else {
        setTerms([]);
      }

    } catch (err) {
      console.error('Error fetching taxonomy data:', err);
      setError(`Failed to load taxonomy: ${err.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchTaxonomy();
      fetchLocales();
    } else {
      setError('No taxonomy ID provided');
      setLoading(false);
    }
  }, [id]);

  // Validation functions
  const validateTaxonomy = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Taxonomy name is required';
    } else if (formData.name.length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    }
    
    if (!formData.slug.trim()) {
      newErrors.slug = 'Slug is required';
    } else if (!/^[a-z0-9-]+$/.test(formData.slug)) {
      newErrors.slug = 'Slug can only contain lowercase letters, numbers, and hyphens';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateTerm = () => {
    const newErrors = {};
    
    if (!termFormData.slug.trim()) {
      newErrors.slug = 'Slug is required';
    } else if (!/^[a-z0-9-]+$/.test(termFormData.slug)) {
      newErrors.slug = 'Slug can only contain lowercase letters, numbers, and hyphens';
    }
    
    // Validate that at least one translation has a name
    const hasValidTranslation = termFormData.translations.some(t => t.name && t.name.trim());
    if (!hasValidTranslation) {
      newErrors.translations = 'At least one translation with a name is required';
    }
    
    // Validate individual translations
    termFormData.translations.forEach((translation, index) => {
      if (translation.name && translation.name.length < 2) {
        newErrors[`translation_${index}_name`] = 'Name must be at least 2 characters';
      }
    });
    
    setTermErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleNameChange = (e) => {
    const name = e.target.value;
    // Auto-generate slug if name changes and slug was empty or auto-generated previously
    if (!formData.slug || formData.slug === generateSlug(formData.name)) {
      setFormData({
        ...formData,
        name,
        slug: generateSlug(name)
      });
    } else {
      setFormData({
        ...formData,
        name
      });
    }
    
    // Clear error
    if (errors.name) {
      setErrors(prev => ({ ...prev, name: '' }));
    }
  };

  const handleSlugChange = (e) => {
    const slug = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-');
    setFormData({
      ...formData,
      slug
    });
    
    // Clear error
    if (errors.slug) {
      setErrors(prev => ({ ...prev, slug: '' }));
    }
  };

  // Handle term input changes
  const handleTermInputChange = (e) => {
    const { name, value } = e.target;
    setTermFormData({
      ...termFormData,
      [name]: value
    });
    
    // Clear error when user starts typing
    if (termErrors[name]) {
      setTermErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleTermSlugChange = (e) => {
    const slug = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-');
    setTermFormData({
      ...termFormData,
      slug
    });
    
    // Clear error
    if (termErrors.slug) {
      setTermErrors(prev => ({ ...prev, slug: '' }));
    }
  };

  // Handle translation changes
  const handleTranslationChange = (index, field, value) => {
    const updatedTranslations = [...termFormData.translations];
    updatedTranslations[index] = {
      ...updatedTranslations[index],
      [field]: value
    };

    // Auto-generate slug from first translation name if slug is empty
    if (field === 'name' && value && !termFormData.slug) {
      setTermFormData({
        ...termFormData,
        translations: updatedTranslations,
        slug: generateSlug(value)
      });
    } else {
      setTermFormData({
        ...termFormData,
        translations: updatedTranslations
      });
    }
    
    // Clear translation errors
    if (termErrors[`translation_${index}_${field}`]) {
      setTermErrors(prev => ({ ...prev, [`translation_${index}_${field}`]: '' }));
    }
    if (termErrors.translations) {
      setTermErrors(prev => ({ ...prev, translations: '' }));
    }
  };

  const addTranslation = () => {
    // Find locale that doesn't have a translation yet
    const usedLocaleIds = termFormData.translations.map(t => t.locale_id);
    const availableLocale = locales.find(l => !usedLocaleIds.includes(l.id));
    
    if (availableLocale) {
      setTermFormData({
        ...termFormData,
        translations: [
          ...termFormData.translations,
          {
            locale_id: availableLocale.id,
            name: '',
            description: '',
            meta_title: '',
            meta_description: '',
            custom_fields: {}
          }
        ]
      });
    }
  };

  const removeTranslation = (index) => {
    const updatedTranslations = termFormData.translations.filter((_, i) => i !== index);
    setTermFormData({
      ...termFormData,
      translations: updatedTranslations
    });
  };

  const copySlug = (slug) => {
    navigator.clipboard.writeText(slug);
    toast({
      title: 'Copied!',
      description: 'Slug copied to clipboard',
      status: 'success',
      duration: 2000,
      isClosable: true,
    });
  };

  const handleSaveTaxonomy = async (e) => {
    e.preventDefault();
    
    if (!validateTaxonomy()) return;

    try {
      setSavingTaxonomy(true);
      await apiClient(`/taxonomies/${tenantId}/${id}`, {
        method: 'PUT',
        body: JSON.stringify({
          name: formData.name,
          slug: formData.slug,
          description: formData.description
        })
      });
      
      toast({
        title: 'Success!',
        description: "Taxonomy updated successfully",
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
      await fetchTaxonomy(); // Refresh data
    } catch (err) {
      console.error('Error updating taxonomy:', err);
      toast({
        title: 'Update Failed',
        description: err.message || "There was an error updating the taxonomy",
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSavingTaxonomy(false);
    }
  };

  const handleAddTerm = () => {
    // Initialize with default translation for the first available locale
    const defaultTranslations = locales.length > 0 ? [{
      locale_id: locales[0].id,
      name: '',
      description: '',
      meta_title: '',
      meta_description: '',
      custom_fields: {}
    }] : [];

    setTermFormData({
      slug: '',
      parent_id: null,
      sort_order: 0,
      status: 'active',
      translations: defaultTranslations
    });
    setEditingTermId(null);
    setTermErrors({});
    onOpen();
  };

  const handleEditTerm = (term) => {
    // Ensure term exists and has basic properties
    if (!term) {
      toast({
        title: 'Error',
        description: 'Invalid term data',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setTermFormData({
      slug: term.slug || '',
      parent_id: term.parent_id || null,
      sort_order: term.sort_order || 0,
      status: term.status || 'active',
      translations: Array.isArray(term.translations) ? term.translations.filter(t => t) : []
    });
    setEditingTermId(term.id);
    setTermErrors({});
    onOpen();
  };

  const handleSaveTerm = async () => {
    if (!validateTerm()) return;

    try {
      setSavingTerm(true);
      
      const payload = {
        tenant_id: tenantId,
        slug: termFormData.slug,
        parent_id: termFormData.parent_id,
        sort_order: parseInt(termFormData.sort_order) || 0,
        status: termFormData.status,
        translations: termFormData.translations.filter(t => t.name && t.name.trim())
      };

      if (editingTermId) {
        // Update existing term
        await apiClient(`/terms/${tenantId}/${editingTermId}`, {
          method: 'PUT',
          body: JSON.stringify(payload)
        });
        
        toast({
          title: 'Success!',
          description: "Term updated successfully",
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        // Create new term
        await apiClient(`/taxonomies/${tenantId}/${id}/terms`, {
          method: 'POST',
          body: JSON.stringify(payload)
        });
        
        toast({
          title: 'Success!',
          description: "Term created successfully",
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }
      
      // Refresh terms list
      const termsData = await apiClient(`/taxonomies/${tenantId}/${id}/terms`);
      setTerms((termsData && termsData.terms) ? termsData.terms : []);
      
      onClose();
      
    } catch (err) {
      console.error('Error saving term:', err);
      toast({
        title: editingTermId ? 'Update Failed' : 'Creation Failed',
        description: err.message || `There was an error ${editingTermId ? 'updating' : 'creating'} the term`,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSavingTerm(false);
    }
  };

  const confirmDeleteTerm = (term) => {
    if (!term || !term.id) {
      toast({
        title: 'Error',
        description: 'Invalid term selected for deletion',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    setTermToDelete(term);
    setIsDeleteOpen(true);
  };

  const handleDeleteTerm = async () => {
    if (!termToDelete) return;
    
    try {
      await apiClient(`/terms/${tenantId}/${termToDelete.id}`, {
        method: 'DELETE'
      });
      
      toast({
        title: 'Success!',
        description: "Term deleted successfully",
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
      // Refresh terms list
      const termsData = await apiClient(`/taxonomies/${tenantId}/${id}/terms`);
      setTerms((termsData && termsData.terms) ? termsData.terms : []);
      
    } catch (err) {
      console.error('Error deleting term:', err);
      toast({
        title: 'Deletion Failed',
        description: err.message || "There was an error deleting the term",
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsDeleteOpen(false);
      setTermToDelete(null);
    }
  };

  // Get locale name by ID
  const getLocaleName = (localeId) => {
    const locale = locales.find(l => l.id === localeId);
    return locale ? `${locale.name} (${locale.code})` : `Locale ${localeId}`;
  };

  // Get primary translation name for display with proper null checking
  const getPrimaryTranslationName = (term) => {
    // Handle null or undefined term
    if (!term) {
      return 'Unknown Term';
    }
    
    // Handle missing or empty translations array
    if (!term.translations || !Array.isArray(term.translations) || term.translations.length === 0) {
      return term.slug || 'Unnamed Term';
    }
    
    // Find first translation with a name
    const translationWithName = term.translations.find(t => t && t.name && t.name.trim());
    
    if (translationWithName) {
      return translationWithName.name;
    }
    
    // Fallback to slug or default name
    return term.slug || 'Unnamed Term';
  };

  // Helper function to validate term data
  const isValidTerm = (term) => {
    return term && 
           typeof term === 'object' && 
           term.id && 
           (term.slug || (term.translations && Array.isArray(term.translations) && term.translations.some(t => t && t.name && t.name.trim())));
  };

  // Loading State
  if (loading) {
    return (
      <Box minH="100vh" bg={bgColor} py={8}>
        <Container maxW="container.xl">
          <Skeleton height="40px" width="200px" mb={6} />
          <Skeleton height="60px" width="400px" mb={8} />
          
          <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={8}>
            <Box gridColumn={{ base: 1, lg: "1 / 3" }}>
              <Card>
                <CardBody>
                  <VStack spacing={6}>
                    <SkeletonText noOfLines={2} spacing="4" />
                    <Skeleton height="40px" />
                    <Skeleton height="40px" />
                    <SkeletonText noOfLines={3} spacing="4" />
                  </VStack>
                </CardBody>
              </Card>
            </Box>
            <Box>
              <Skeleton height="200px" />
            </Box>
          </SimpleGrid>
        </Container>
      </Box>
    );
  }

  // Error State
  if (error) {
    return (
      <Box minH="100vh" bg={bgColor} py={8}>
        <Container maxW="container.xl">
          <Button 
            leftIcon={<ArrowBackIcon />} 
            variant="ghost" 
            mb={6}
            as={RouterLink}
            to="/taxonomies"
            size="lg"
          >
            Back to Taxonomies
          </Button>
          
          <Alert status="error" borderRadius="md">
            <AlertIcon />
            <Box>
              <Text fontWeight="semibold">Failed to Load Taxonomy</Text>
              <Text>{error}</Text>
            </Box>
          </Alert>
        </Container>
      </Box>
    );
  }

  if (!taxonomy) {
    return (
      <Box minH="100vh" bg={bgColor} py={8}>
        <Container maxW="container.xl">
          <Button 
            leftIcon={<ArrowBackIcon />} 
            variant="ghost" 
            mb={6}
            as={RouterLink}
            to="/taxonomies"
            size="lg"
          >
            Back to Taxonomies
          </Button>
          
          <Alert status="warning" borderRadius="md">
            <AlertIcon />
            <Box>
              <Text fontWeight="semibold">Taxonomy Not Found</Text>
              <Text>The requested taxonomy could not be loaded.</Text>
            </Box>
          </Alert>
        </Container>
      </Box>
    );
  }

  return (
    <Box minH="100vh" bg={bgColor} py={8}>
      <Container maxW="container.xl">
        {/* Header */}
        <MotionBox
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Button 
            leftIcon={<ArrowBackIcon />} 
            variant="ghost" 
            mb={6}
            as={RouterLink}
            to="/taxonomies"
            size="lg"
          >
            Back to Taxonomies
          </Button>
          
          <Flex align="center" justify="space-between" mb={8}>
            <Box>
              <Heading size="xl" mb={2}>Edit Taxonomy</Heading>
              <Text color={textColor} fontSize="lg">
                {taxonomy.name}
              </Text>
            </Box>
            <Badge colorScheme="blue" fontSize="md" p={2} borderRadius="md">
              {terms.length} Terms
            </Badge>
          </Flex>
        </MotionBox>

        {/* Main Content */}
        <Tabs variant="enclosed" colorScheme="blue" size="lg">
          <TabList mb={6}>
            <Tab>General Settings</Tab>
            <Tab>
              Terms 
              <Badge ml={2} colorScheme="blue" borderRadius="full">
                {terms.length}
              </Badge>
            </Tab>
          </TabList>
          
          <TabPanels>
            {/* General Settings Panel */}
            <TabPanel px={0}>
              <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={8}>
                {/* Form Column */}
                <Box gridColumn={{ base: 1, lg: "1 / 3" }}>
                  <MotionCard
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    bg={cardBg}
                    shadow="lg"
                  >
                    <CardHeader>
                      <Heading size="md">Basic Information</Heading>
                      <Text color={textColor} mt={1}>
                        Update the core properties of your taxonomy
                      </Text>
                    </CardHeader>
                    <CardBody>
                      <Box as="form" onSubmit={handleSaveTaxonomy}>
                        <VStack spacing={6} align="stretch">
                          {/* Name Field */}
                          <FormControl isRequired isInvalid={!!errors.name}>
                            <FormLabel fontSize="md" fontWeight="semibold">
                              <Flex align="center">
                                <EditIcon mr={2} />
                                Taxonomy Name
                              </Flex>
                            </FormLabel>
                            <Input 
                              name="name"
                              placeholder="Enter taxonomy name" 
                              value={formData.name}
                              onChange={handleNameChange}
                              size="lg"
                              focusBorderColor="blue.500"
                            />
                            <FormHelperText>
                              The name is how the taxonomy appears in the admin dashboard
                            </FormHelperText>
                            <FormErrorMessage>{errors.name}</FormErrorMessage>
                          </FormControl>
                          
                          {/* Slug Field */}
                          <FormControl isInvalid={!!errors.slug}>
                            <FormLabel fontSize="md" fontWeight="semibold">
                              <Flex align="center">
                                <LinkIcon mr={2} />
                                URL Slug
                              </Flex>
                            </FormLabel>
                            <InputGroup size="lg">
                              <InputLeftElement pointerEvents="none">
                                <Text fontSize="sm" color={textColor}>/</Text>
                              </InputLeftElement>
                              <Input 
                                name="slug"
                                placeholder="taxonomy-slug" 
                                value={formData.slug}
                                onChange={handleSlugChange}
                                focusBorderColor="blue.500"
                              />
                              <InputRightElement>
                                <Tooltip label="Copy slug">
                                  <IconButton
                                    icon={<CopyIcon />}
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => copySlug(formData.slug)}
                                    isDisabled={!formData.slug}
                                  />
                                </Tooltip>
                              </InputRightElement>
                            </InputGroup>
                            <FormHelperText>
                              The slug is the URL-friendly version of the name
                            </FormHelperText>
                            <FormErrorMessage>{errors.slug}</FormErrorMessage>
                          </FormControl>
                          
                          {/* Description Field */}
                          <FormControl>
                            <FormLabel fontSize="md" fontWeight="semibold">
                              Description (Optional)
                            </FormLabel>
                            <Textarea 
                              name="description"
                              value={formData.description}
                              onChange={handleInputChange}
                              placeholder="Describe the purpose and usage of this taxonomy"
                              rows={4}
                              resize="vertical"
                              focusBorderColor="blue.500"
                            />
                            <FormHelperText>
                              Help others understand when and how to use this taxonomy
                            </FormHelperText>
                          </FormControl>
                          
                          {/* Metadata */}
                          {(taxonomy.created_at || taxonomy.updated_at) && (
                            <Box>
                              <Divider mb={4} />
                              <Text fontSize="sm" color={textColor} mb={2}>
                                <Text as="span" fontWeight="semibold">Metadata</Text>
                              </Text>
                              <Stack spacing={1}>
                                {taxonomy.created_at && (
                                  <Text fontSize="sm" color={textColor}>
                                    Created: {new Date(taxonomy.created_at).toLocaleString()}
                                  </Text>
                                )}
                                {taxonomy.updated_at && (
                                  <Text fontSize="sm" color={textColor}>
                                    Last modified: {new Date(taxonomy.updated_at).toLocaleString()}
                                  </Text>
                                )}
                              </Stack>
                            </Box>
                          )}
                          
                          {/* Action Buttons */}
                          <HStack w="100%" justify="flex-end" pt={6} spacing={4}>
                            <Button 
                              variant="outline" 
                              size="lg"
                              as={RouterLink} 
                              to="/taxonomies"
                              isDisabled={savingTaxonomy}
                            >
                              Cancel
                            </Button>
                            <Button 
                              colorScheme="blue" 
                              type="submit" 
                              size="lg"
                              isLoading={savingTaxonomy}
                              loadingText="Saving..."
                              isDisabled={!formData.name}
                              leftIcon={!savingTaxonomy ? <CheckIcon /> : undefined}
                            >
                              Save Changes
                            </Button>
                          </HStack>
                        </VStack>
                      </Box>
                    </CardBody>
                  </MotionCard>
                </Box>

                {/* Info Sidebar */}
                <Box>
                  <MotionCard
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                    bg={cardBg}
                  >
                    <CardHeader>
                      <Flex align="center">
                        <Icon as={ViewIcon} color="blue.500" mr={2} />
                        <Heading size="sm">Preview</Heading>
                      </Flex>
                    </CardHeader>
                    <CardBody pt={0}>
                      <VStack align="stretch" spacing={3}>
                        <Box>
                          <Text fontSize="xs" color={textColor} textTransform="uppercase" letterSpacing="wide">
                            Display Name
                          </Text>
                          <Text fontWeight="semibold">{formData.name || 'Untitled'}</Text>
                        </Box>
                        <Box>
                          <Text fontSize="xs" color={textColor} textTransform="uppercase" letterSpacing="wide">
                            URL Slug
                          </Text>
                          <Text fontFamily="mono" fontSize="sm" color="blue.500">
                            /{formData.slug || 'slug'}
                          </Text>
                        </Box>
                        <Box>
                          <Text fontSize="xs" color={textColor} textTransform="uppercase" letterSpacing="wide">
                            Terms Count
                          </Text>
                          <Text fontWeight="semibold">{terms.length} terms</Text>
                        </Box>
                        <Box>
                          <Text fontSize="xs" color={textColor} textTransform="uppercase" letterSpacing="wide">
                            Available Locales
                          </Text>
                          <Text fontWeight="semibold">{locales.length} locales</Text>
                        </Box>
                      </VStack>
                    </CardBody>
                  </MotionCard>
                </Box>
              </SimpleGrid>
            </TabPanel>
            
            {/* Terms Panel */}
            <TabPanel px={0}>
              <MotionBox
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <Card bg={cardBg} shadow="lg">
                  <CardHeader>
                    <Flex justify="space-between" align="center">
                      <Box>
                        <Heading size="md">Manage Terms</Heading>
                        <Text color={textColor} mt={1}>
                          Create and organize multilingual terms for this taxonomy
                        </Text>
                      </Box>
                      <Button 
                        leftIcon={<AddIcon />} 
                        colorScheme="blue" 
                        onClick={handleAddTerm}
                        size="lg"
                        isDisabled={locales.length === 0}
                      >
                        Add Term
                      </Button>
                    </Flex>
                  </CardHeader>
                  <CardBody>
                    {locales.length === 0 ? (
                      <Alert status="warning" borderRadius="md">
                        <AlertIcon />
                        <Box>
                          <Text fontWeight="semibold">No Locales Available</Text>
                          <Text>You need to set up locales before creating multilingual terms.</Text>
                        </Box>
                      </Alert>
                    ) : terms.length === 0 ? (
                      <Box textAlign="center" py={12}>
                        <Icon as={InfoIcon} boxSize={12} color="gray.400" mb={4} />
                        <Text fontSize="lg" fontWeight="semibold" mb={2}>
                          No terms yet
                        </Text>
                        <Text color={textColor} mb={6}>
                          Terms are the individual items within your taxonomy. 
                          Add your first multilingual term to get started.
                        </Text>
                        <Button 
                          leftIcon={<AddIcon />} 
                          colorScheme="blue" 
                          onClick={handleAddTerm}
                          size="lg"
                        >
                          Add Your First Term
                        </Button>
                      </Box>
                    ) : (
                      <SimpleGrid columns={1} spacing={4}>
                        <AnimatePresence>
                          {terms
                            .filter(term => term && term.id) // Filter out any invalid terms
                            .map((term) => (
                              <MotionFlex
                                key={term.id}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -20 }}
                                transition={{ duration: 0.3 }}
                                p={6} 
                                borderWidth="1px" 
                                borderRadius="lg"
                                direction="column"
                                _hover={{ bg: hoverBg, transform: 'translateY(-2px)' }}
                                cursor="pointer"
                              >
                                <Flex justify="space-between" align="flex-start" mb={4}>
                                  <Box flex="1">
                                    <Text fontWeight="bold" fontSize="lg" mb={1}>
                                      {getPrimaryTranslationName(term)}
                                    </Text>
                                    <Text fontSize="sm" color="blue.500" fontFamily="mono" mb={2}>
                                      /{term.slug || 'no-slug'}
                                    </Text>
                                    <HStack spacing={2} mb={2}>
                                      <Badge colorScheme={term.status === 'active' ? 'green' : 'red'}>
                                        {term.status || 'unknown'}
                                      </Badge>
                                      <Badge colorScheme="blue">
                                        {(term.translations && Array.isArray(term.translations)) 
                                          ? term.translations.filter(t => t && t.name).length 
                                          : 0} translations
                                      </Badge>
                                      {term.sort_order !== null && term.sort_order !== undefined && (
                                        <Badge colorScheme="gray">
                                          Order: {term.sort_order}
                                        </Badge>
                                      )}
                                    </HStack>
                                  </Box>
                                  
                                  <Menu>
                                    <MenuButton
                                      as={IconButton}
                                      icon={<ChevronDownIcon />}
                                      variant="ghost"
                                      size="sm"
                                    />
                                    <MenuList>
                                      <MenuItem
                                        icon={<EditIcon />}
                                        onClick={() => handleEditTerm(term)}
                                      >
                                        Edit Term
                                      </MenuItem>
                                      <MenuItem
                                        icon={<CopyIcon />}
                                        onClick={() => copySlug(term.slug || '')}
                                      >
                                        Copy Slug
                                      </MenuItem>
                                      <MenuItem
                                        icon={<DeleteIcon />}
                                        onClick={() => confirmDeleteTerm(term)}
                                        color="red.500"
                                      >
                                        Delete Term
                                      </MenuItem>
                                    </MenuList>
                                  </Menu>
                                </Flex>

                                {/* Translations Display with improved error handling */}
                                {term.translations && Array.isArray(term.translations) && term.translations.length > 0 ? (
                                  <Box>
                                    <Text fontSize="sm" fontWeight="semibold" mb={2}>
                                      Translations:
                                    </Text>
                                    <Wrap spacing={2}>
                                      {term.translations
                                        .filter(translation => translation && translation.name && translation.name.trim())
                                        .map((translation, index) => (
                                          <WrapItem key={`${term.id}-${translation.locale_id}-${index}`}>
                                            <Box
                                              p={2}
                                              borderWidth="1px"
                                              borderRadius="md"
                                              fontSize="sm"
                                            >
                                              <Text fontWeight="medium">
                                                {getLocaleName(translation.locale_id)}
                                              </Text>
                                              <Text color={textColor}>
                                                {translation.name}
                                              </Text>
                                            </Box>
                                          </WrapItem>
                                        ))}
                                    </Wrap>
                                    {term.translations.filter(t => t && t.name && t.name.trim()).length === 0 && (
                                      <Text fontSize="sm" color="orange.500" fontStyle="italic">
                                        No valid translations available
                                      </Text>
                                    )}
                                  </Box>
                                ) : (
                                  <Box>
                                    <Text fontSize="sm" color={textColor} fontStyle="italic">
                                      No translations available
                                    </Text>
                                  </Box>
                                )}
                              </MotionFlex>
                            ))}
                        </AnimatePresence>
                      </SimpleGrid>
                    )}
                  </CardBody>
                </Card>
              </MotionBox>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Container>
      
      {/* Term Edit Modal - Updated for Multilingual */}
      <Modal isOpen={isOpen} onClose={onClose} size="6xl">
        <ModalOverlay backdropFilter="blur(4px)" />
        <ModalContent maxH="90vh" overflowY="auto">
          <ModalHeader>
            <Flex align="center">
              <Icon as={editingTermId ? EditIcon : AddIcon} mr={2} />
              {editingTermId ? 'Edit Multilingual Term' : 'Add New Multilingual Term'}
            </Flex>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={6} align="stretch">
              {/* Basic Term Info */}
              <Card>
                <CardHeader>
                  <Heading size="sm">Basic Information</Heading>
                </CardHeader>
                <CardBody>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <FormControl isRequired isInvalid={!!termErrors.slug}>
                      <FormLabel fontSize="md" fontWeight="semibold">
                        Term Slug
                      </FormLabel>
                      <InputGroup size="lg">
                        <InputLeftElement pointerEvents="none">
                          <Text fontSize="sm" color={textColor}>/</Text>
                        </InputLeftElement>
                        <Input 
                          name="slug"
                          value={termFormData.slug}
                          onChange={handleTermSlugChange}
                          placeholder="term-slug"
                          focusBorderColor="blue.500"
                        />
                        <InputRightElement>
                          <Tooltip label="Copy slug">
                            <IconButton
                              icon={<CopyIcon />}
                              size="sm"
                              variant="ghost"
                              onClick={() => copySlug(termFormData.slug)}
                              isDisabled={!termFormData.slug}
                            />
                          </Tooltip>
                        </InputRightElement>
                      </InputGroup>
                      <FormHelperText>
                        Unique identifier for this term
                      </FormHelperText>
                      <FormErrorMessage>{termErrors.slug}</FormErrorMessage>
                    </FormControl>

                    <FormControl>
                      <FormLabel fontSize="md" fontWeight="semibold">
                        Sort Order
                      </FormLabel>
                      <Input 
                        name="sort_order"
                        type="number"
                        value={termFormData.sort_order}
                        onChange={handleTermInputChange}
                        placeholder="0"
                        size="lg"
                        focusBorderColor="blue.500"
                      />
                      <FormHelperText>
                        Order for displaying terms (lower numbers first)
                      </FormHelperText>
                    </FormControl>

                    <FormControl>
                      <FormLabel fontSize="md" fontWeight="semibold">
                        Status
                      </FormLabel>
                      <Select 
                        name="status"
                        value={termFormData.status}
                        onChange={handleTermInputChange}
                        size="lg"
                        focusBorderColor="blue.500"
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </Select>
                      <FormHelperText>
                        Active terms are visible in the frontend
                      </FormHelperText>
                    </FormControl>

                    <FormControl>
                      <FormLabel fontSize="md" fontWeight="semibold">
                        Parent Term (Optional)
                      </FormLabel>
                      <Select 
                        name="parent_id"
                        value={termFormData.parent_id || ''}
                        onChange={handleTermInputChange}
                        size="lg"
                        focusBorderColor="blue.500"
                      >
                        <option value="">No Parent</option>
                        {terms
                          .filter(t => {
                            // Filter out invalid terms and the current term being edited
                            return t && 
                                   t.id && 
                                   t.id !== editingTermId && 
                                   (t.slug || (t.translations && t.translations.some(trans => trans && trans.name)));
                          })
                          .map(term => (
                            <option key={term.id} value={term.id}>
                              {getPrimaryTranslationName(term)}
                            </option>
                          ))}
                      </Select>
                      <FormHelperText>
                        Create hierarchical relationships
                      </FormHelperText>
                    </FormControl>
                  </SimpleGrid>
                </CardBody>
              </Card>

              {/* Translations */}
              <Card>
                <CardHeader>
                  <Flex justify="space-between" align="center">
                    <Box>
                      <Heading size="sm">Translations</Heading>
                      <Text fontSize="sm" color={textColor}>
                        Add translations for different languages
                      </Text>
                    </Box>
                    <Button
                      leftIcon={<AddIcon />}
                      size="sm"
                      colorScheme="blue"
                      variant="outline"
                      onClick={addTranslation}
                      isDisabled={termFormData.translations.length >= locales.length}
                    >
                      Add Translation
                    </Button>
                  </Flex>
                  {termErrors.translations && (
                    <Text color="red.500" fontSize="sm" mt={2}>
                      {termErrors.translations}
                    </Text>
                  )}
                </CardHeader>
                <CardBody>
                  {termFormData.translations.length === 0 ? (
                    <Box textAlign="center" py={8}>
                      <Text color={textColor} mb={4}>
                        No translations added yet. Click "Add Translation" to get started.
                      </Text>
                      <Button
                        leftIcon={<AddIcon />}
                        colorScheme="blue"
                        variant="outline"
                        onClick={addTranslation}
                        isDisabled={locales.length === 0}
                      >
                        Add First Translation
                      </Button>
                    </Box>
                  ) : (
                    <Accordion allowMultiple defaultIndex={[0]}>
                      {termFormData.translations.map((translation, index) => {
                        const locale = locales.find(l => l.id === translation.locale_id);
                        return (
                          <AccordionItem key={index}>
                            <AccordionButton>
                              <Box flex="1" textAlign="left">
                                <Flex align="center" justify="space-between">
                                  <Text fontWeight="semibold">
                                    {locale ? `${locale.name} (${locale.code})` : `Locale ${translation.locale_id}`}
                                  </Text>
                                  <HStack spacing={2}>
                                    {translation.name && (
                                      <Badge colorScheme="green">Has Name</Badge>
                                    )}
                                    <IconButton
                                      icon={<DeleteIcon />}
                                      size="xs"
                                      colorScheme="red"
                                      variant="ghost"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        removeTranslation(index);
                                      }}
                                    />
                                  </HStack>
                                </Flex>
                              </Box>
                              <AccordionIcon />
                            </AccordionButton>
                            <AccordionPanel pb={4}>
                              <VStack spacing={4} align="stretch">
                                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                                  <FormControl isRequired isInvalid={!!termErrors[`translation_${index}_name`]}>
                                    <FormLabel>Name</FormLabel>
                                    <Input 
                                      value={translation.name}
                                      onChange={(e) => handleTranslationChange(index, 'name', e.target.value)}
                                      placeholder="Enter term name"
                                      focusBorderColor="blue.500"
                                    />
                                    <FormErrorMessage>
                                      {termErrors[`translation_${index}_name`]}
                                    </FormErrorMessage>
                                  </FormControl>

                                  <FormControl>
                                    <FormLabel>Locale</FormLabel>
                                    <Select 
                                      value={translation.locale_id}
                                      onChange={(e) => handleTranslationChange(index, 'locale_id', parseInt(e.target.value))}
                                      focusBorderColor="blue.500"
                                    >
                                      {locales.map(locale => (
                                        <option key={locale.id} value={locale.id}>
                                          {locale.name} ({locale.code})
                                        </option>
                                      ))}
                                    </Select>
                                  </FormControl>
                                </SimpleGrid>

                                <FormControl>
                                  <FormLabel>Description</FormLabel>
                                  <Textarea 
                                    value={translation.description}
                                    onChange={(e) => handleTranslationChange(index, 'description', e.target.value)}
                                    placeholder="Enter term description"
                                    rows={3}
                                    focusBorderColor="blue.500"
                                  />
                                </FormControl>

                                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                                  <FormControl>
                                    <FormLabel>Meta Title</FormLabel>
                                    <Input 
                                      value={translation.meta_title}
                                      onChange={(e) => handleTranslationChange(index, 'meta_title', e.target.value)}
                                      placeholder="SEO meta title"
                                      focusBorderColor="blue.500"
                                    />
                                  </FormControl>

                                  <FormControl>
                                    <FormLabel>Meta Description</FormLabel>
                                    <Input 
                                      value={translation.meta_description}
                                      onChange={(e) => handleTranslationChange(index, 'meta_description', e.target.value)}
                                      placeholder="SEO meta description"
                                      focusBorderColor="blue.500"
                                    />
                                  </FormControl>
                                </SimpleGrid>
                              </VStack>
                            </AccordionPanel>
                          </AccordionItem>
                        );
                      })}
                    </Accordion>
                  )}
                </CardBody>
              </Card>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose} isDisabled={savingTerm}>
              Cancel
            </Button>
            <Button 
              colorScheme="blue" 
              onClick={handleSaveTerm}
              isLoading={savingTerm}
              loadingText="Saving..."
              isDisabled={!termFormData.slug || termFormData.translations.length === 0}
              leftIcon={!savingTerm ? <CheckIcon /> : undefined}
            >
              {editingTermId ? 'Update Term' : 'Create Term'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        isOpen={isDeleteOpen}
        leastDestructiveRef={cancelRef}
        onClose={() => setIsDeleteOpen(false)}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              <Flex align="center">
                <Icon as={WarningIcon} color="red.500" mr={2} />
                Delete Term
              </Flex>
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to delete the term{' '}
              <Text as="span" fontWeight="bold">
                "{getPrimaryTranslationName(termToDelete)}"
              </Text>? 
              This action cannot be undone and will delete all translations. This may affect content that uses this term.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={() => setIsDeleteOpen(false)}>
                Cancel
              </Button>
              <Button 
                colorScheme="red" 
                onClick={handleDeleteTerm} 
                ml={3}
                isDisabled={!termToDelete} // Disable if no term selected
              >
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default TaxonomyEdit;
